/**
 * Who may use Ask Moo, and where they land. OMNI-484 / OMNI-485 / OMNI-533.
 *
 * Cowbell sees the full agent catalog. Brokers (AGENCY) and policyholders
 * (CUSTOMER) see a small, server-restricted catalog — the chat-agent backend
 * gates it on the JWT `at` claim (AGENCY: Cyber Pulse; CUSTOMER: Cyber Pulse +
 * the Risk Insights Advisor — OMNI-485). Carriers (CARRIER) see ONLY the
 * carrier-scoped ML UW Agent, gated the same way (`accountTypes: [CARRIER]` —
 * OMNI-533). These helpers only drive the console UX (mount + button
 * visibility + which agent a restricted persona is allowed to sit on); the
 * backend remains the authority on the catalog contents.
 */
export const CYBER_PULSE_AGENT_ID = 'cyber-pulse';
export const ML_UW_CARRIER_AGENT_ID = 'ml-underwriter-carrier';

export interface AskMooActor {
  isCowbell: boolean;
  isAgency: boolean;
  isInsured: boolean;
  isCarrier: boolean;
}

/** Personas allowed to open the Ask Moo entry points. */
export const canUseAskMoo = (actor: AskMooActor): boolean =>
  actor.isCowbell || actor.isAgency || actor.isInsured || actor.isCarrier;

/**
 * Personas allowed to reach Moo Studio (agent/skill/tool authoring). Cowbell
 * only. `isCowbell` reflects the account type the user is *acting in* (the JWT
 * `at` claim), so a Cowbell employee jumped into another account (AGENCY /
 * CUSTOMER / CARRIER) is correctly excluded — they act as that account type
 * and must not see Studio. The `/moo/studio` routes already redirect
 * non-Cowbell (see MooRoutesWrapper); this helper hides the entry points so
 * they aren't shown an affordance that dead-ends in that redirect.
 */
export const canUseMooStudio = (actor: AskMooActor): boolean => actor.isCowbell;

/**
 * Personas whose Ask Moo catalog is server-restricted to a small fixed set
 * (they never get the full Cowbell catalog or custom agents). Such a persona
 * must be kept ON an agent from their own catalog — a stale sessionStorage id
 * or a saved-prefs default carried over from a Cowbell session could otherwise
 * strand them on an agent they can no longer see. AGENCY, CUSTOMER, and
 * CARRIER (OMNI-588: carriers were originally left out on the assumption they
 * were "handled separately", but nothing handled them — after an in-app jump
 * into a carrier account the panel kept `default-moo` or a stale Cowbell agent
 * id and the header fell back to the literal "Moo").
 */
export const hasRestrictedCatalog = (actor: AskMooActor): boolean =>
  actor.isAgency || actor.isInsured || actor.isCarrier;

/**
 * The agent a restricted persona is pinned to when the catalog can't inform
 * the choice (settled empty: fetch error or empty payload). Cyber Pulse is the
 * universal broker/policyholder agent, but it is NOT in the carrier catalog —
 * a carrier fails safe to the carrier-scoped ML UW Agent instead (OMNI-588).
 * Either way the guarantee is the same: never leave a restricted persona
 * sitting on a possibly-Cowbell agent.
 */
export const restrictedFailsafeAgentId = (actor: AskMooActor): string =>
  actor.isCarrier ? ML_UW_CARRIER_AGENT_ID : CYBER_PULSE_AGENT_ID;

/**
 * For a restricted-catalog persona, decide which agent they should be on given
 * the currently-selected agent and the account-type-filtered, backend-ordered
 * catalog:
 *
 *  - the current agent, if it's in the catalog — a valid in-catalog selection
 *    is kept. This is what lets a policyholder switch freely between Cyber
 *    Pulse and the Risk Insights Advisor (OMNI-485); the old clamp hard-pinned
 *    to `cyber-pulse` and silently reverted every other choice.
 *  - otherwise the catalog's first entry — its account-type default (the
 *    backend orders the default first via `defaultForAccountTypes`, OMNI-485).
 *    This is where a stale or now-gated selection is reset to, preserving the
 *    OMNI-484 "can't be stranded on a gated agent" guarantee.
 *
 * Returns `null` while the catalog is empty (still loading) so callers don't
 * bounce the selection before the valid set is known.
 */
export function resolveRestrictedAgentId(
  currentAgentId: string,
  catalogAgentIds: readonly string[]
): string | null {
  if (catalogAgentIds.length === 0) return null;
  return catalogAgentIds.includes(currentAgentId)
    ? currentAgentId
    : catalogAgentIds[0];
}

/**
 * Reads the `limit_moo_agents` GrowthBook payload — a JSON flag whose value is
 * an allow-list of human-readable chat agent ids:
 *
 *     { "allowedAgents": ["risk-insights-advisor", "ml-underwriter-carrier"] }
 *
 * Only policyholder and carrier agents belong in it: policyholders get
 * `risk-insights-advisor`, carriers get `ml-underwriter-carrier`. Internal
 * Cowbell agents are never limited by this flag.
 *
 * Returns `null` whenever the flag isn't limiting anything (off, no payload,
 * a malformed payload, or an empty list) so callers fall through to the full
 * server catalog untouched — the flag is additive on top of the account-type
 * gating the backend already applies, never a replacement for it.
 *
 * The whole feature fails *open*, by design: this trims the panel down for a
 * specific audience (a demo account), it is not an access control, and the
 * backend still owns who may see what. So anything short of a usable list —
 * including one whose ids match nothing in the catalog, which the library
 * treats the same way — leaves every agent visible rather than risking an
 * empty agent picker.
 *
 * Scope: the Ask Moo chat panel only. Moo Studio's agent lists come from a
 * different query and are deliberately unaffected.
 */
export function parseAllowedAgentIds(value: unknown): readonly string[] | null {
  const allowedAgents = (value as { allowedAgents?: unknown } | null)
    ?.allowedAgents;
  if (!Array.isArray(allowedAgents)) return null;
  const ids = allowedAgents.filter(
    (id): id is string => typeof id === 'string' && id.trim().length > 0
  );
  return ids.length > 0 ? ids : null;
}

// ── OMNI-637: custom-agent authoring (create = account:cowbell; per-agent
// mutate = owner-or-admin; quota = 5/user). The backend enforces all of this;
// these helpers only drive the Moo Studio UX so affordances match the API. ──

/** Max custom agents a non-admin author may own (backend enforces; mirrors it). */
export const MAX_AGENTS_PER_USER = 5;

/** Minimal shape of an agent for the authoring gates. */
export interface AuthorableAgent {
  id?: string;
  category?: string;
  createdBy?: string;
}

/**
 * Owner-or-admin gate for per-agent mutations (edit / delete / publish /
 * duplicating a CUSTOM agent). Owner is keyed by **email** — `createdBy` holds
 * the creator's email (see the "my-agents" filter). Scoped to CUSTOM agents:
 * system agents are never "managed" here (not editable/deletable — not even by
 * a Super Admin; duplicating a system agent is a separate root:root affordance
 * handled by the caller). For a custom agent, a Super Admin manages any;
 * otherwise only the owner. The `custom` check comes first so `isSuperAdmin`
 * can't grant management of a system agent.
 */
export const canManageAgent = (
  agent: AuthorableAgent,
  actorEmail: string | undefined,
  isSuperAdmin: boolean
): boolean =>
  agent.category === 'custom' &&
  (isSuperAdmin ||
    (!!agent.createdBy && !!actorEmail && agent.createdBy === actorEmail));

/**
 * True when `agent` is a custom agent the actor created (owner keyed by email).
 * Single source of truth for "this is one of MY agents" — used by the quota
 * count AND the "My agents" browser tab so ownership is scoped consistently
 * (OMNI-637: the Ask Moo browser tabs previously listed every custom agent, not
 * just the actor's; Studio's tab already scoped by createdBy).
 */
export const isOwnCustomAgent = (
  agent: AuthorableAgent,
  actorEmail: string | undefined
): boolean =>
  agent.category === 'custom' && !!actorEmail && agent.createdBy === actorEmail;

/**
 * Count of custom agents the actor owns — client-side quota check. Counts
 * DISTINCT agent id (not rows): the list can carry both a draft and a live row
 * for the same agent, and the backend quota counts distinct business id — so
 * this must too, or the N/5 badge inflates and Create is wrongly disabled.
 */
export const ownedCustomAgentCount = (
  agents: readonly AuthorableAgent[],
  actorEmail: string | undefined
): number =>
  !actorEmail
    ? 0
    : new Set(
        agents
          .filter((a) => isOwnCustomAgent(a, actorEmail) && !!a.id)
          .map((a) => a.id)
      ).size;

/** True when a non-admin author is at/over the custom-agent cap. */
export const isAtAgentLimit = (
  agents: readonly AuthorableAgent[],
  actorEmail: string | undefined,
  isSuperAdmin: boolean
): boolean =>
  !isSuperAdmin &&
  ownedCustomAgentCount(agents, actorEmail) >= MAX_AGENTS_PER_USER;
